import Box from 'ext-ui/components/Box/Box'
import React, { ReactElement, ReactNode } from 'react'
import * as style from 'src/feature/CardOverviewCardDetails/cardOverviewCardDetails.style'
import {
  type CardDetailsGroupState,
  useCardOverviewCardDetailsData,
} from 'src/feature/CardOverviewCardDetails/useCardOverviewCardDetailsData'
// Shared with the stats roll-up and Recent Transactions so the whole Overview
// tab has ONE navigation idiom and the modifier-click guard (⌘/Ctrl/Shift/Alt,
// non-primary button) lives in one place. Third consumer, imported in place
// rather than promoted — see the EX-40630 plan §11.
import StatNavLink from 'src/feature/CardOverviewStats/StatNavLink'
import { useTranslate } from 'src/localization/Trns'
import type { CardManagementBillingAddress } from 'src/queries/physicalCardManagement/physicalCardManagement.models'

export type CardOverviewCardDetailsProps = {
  /** The card whose billing/company line to read. */
  creditCardId: string
  /**
   * Masked last four, from the already-loaded legacy `CreditCardModel`. Passed
   * as a prop rather than read here: last four and expiration are NOT card
   * management fields, and this feature stays routing- and model-free.
   */
  last4: string
  /** Expiration as `MM/YY`, from `CreditCardModel.expiresFormattedString`. */
  expiration: string
  /**
   * Deep link into Controls & Settings, built by the page (features do not know
   * URLs). Targets the Card settings tile via `settingsAnchors.cardSettings`.
   */
  manageHref: string
  /** SPA navigation for the Manage link. See `StatNavLink`. */
  onNavigate?: (href: string) => void
  className?: string
}

type DetailRowProps = {
  label: string
  value: string
  /** Tabular numerals for masked digits, expiry and ZIP. */
  mono?: boolean
  testId: string
}

/**
 * One label/value pair. A `<div>` inside the panel's `<dl>` — the grouping
 * wrapper the spec allows between `<dl>` and its `<dt>`/`<dd>` pair, matching the
 * `CardSettingsTile` read-only list and `CardOverviewStats`' cells.
 */
function DetailRow(props: DetailRowProps): ReactElement {
  const { label, value, mono, testId } = props

  return (
    <div css={style.row} data-testid={testId}>
      <dt css={style.rowLabel}>{label}</dt>
      <dd css={mono ? style.rowValueMono : style.rowValue}>{value}</dd>
    </div>
  )
}

/**
 * `"New York, NY 10001"` — the locality line under the street address.
 *
 * A null-safe twin of `localityLine` in `CardSettingsTile.tsx`, deliberately not
 * shared: that one takes the FORM type (`BillingAddressValues`, absent parts are
 * `''`) and this one takes the WIRE type (`CardManagementBillingAddress`, absent
 * parts are `null`). Reusing it verbatim here would render "New York, null".
 *
 * Parts are joined rather than templated because address rules vary by country —
 * `province` and `postal` are genuinely absent in some, and a template would
 * leave their separators stranded.
 */
function localityLine(address: CardManagementBillingAddress): string {
  const cityAndProvince = [address.city, address.province]
    .filter((part) => part !== null && part !== '')
    .join(', ')
  return [cityAndProvince, address.postal]
    .filter((part) => part !== null && part !== '')
    .join(' ')
}

type GroupCopy = {
  loading: string
  /** Shown when the denial reason is `ROLE_NOT_ALLOWED`. */
  unavailableRole: string
  unavailable: string
  error: string
}

/**
 * A non-`ready` group's message. `loading` / `unavailable` / `error` are kept
 * distinct on screen (the ticket's sixth bullet), with `role="status"` and
 * `role="alert"` mirroring `CardOverviewRecentTransactions`. Returns `null` when
 * the group is `ready` — the caller renders the real rows instead.
 */
function GroupState(props: {
  state: CardDetailsGroupState
  reasonCode: string | undefined
  copy: GroupCopy
  testId: string
}): ReactElement | null {
  const { state, reasonCode, copy, testId } = props

  if (state === 'loading') {
    return (
      <p
        css={style.groupStatus}
        role="status"
        data-testid={`${testId}-loading`}
      >
        {copy.loading}
      </p>
    )
  }
  if (state === 'unavailable') {
    return (
      <p css={style.groupStatus} data-testid={`${testId}-unavailable`}>
        {reasonCode === 'ROLE_NOT_ALLOWED'
          ? copy.unavailableRole
          : copy.unavailable}
      </p>
    )
  }
  if (state === 'error') {
    return (
      <p css={style.groupStatus} role="alert" data-testid={`${testId}-error`}>
        {copy.error}
      </p>
    )
  }
  return null
}

/**
 * Read-only "Card details" panel on the card Overview tab (EX-40630): masked
 * card credentials, billing contact, and the company line, with a deep link into
 * Controls & Settings.
 *
 * Presentational and routing-free — the mounting page builds `manageHref` and
 * owns SPA navigation. Card number and expiration come from the already-loaded
 * card and render in every state; billing and company line are gated on their
 * OWN capabilities via `useCardOverviewCardDetailsData` and each shows its own
 * loading / unavailable / error message.
 *
 * This panel is deliberately NOT a credential surface: the card number is a
 * permanent last-four mask and no CVC is shown at all. Reveal stays owned by
 * `CardHero` in the header, which the footer points at.
 */
export default function CardOverviewCardDetails(
  props: CardOverviewCardDetailsProps,
): ReactElement {
  const { creditCardId, last4, expiration, manageHref, onNavigate, className } =
    props

  const translate = useTranslate()
  const {
    billingContact,
    billingState,
    billingReasonCode,
    companyLine,
    companyLineState,
    companyLineReasonCode,
  } = useCardOverviewCardDetailsData(creditCardId)

  const billingCopy: GroupCopy = {
    loading: translate('Loading billing details'),
    unavailableRole: translate(
      'You do not have permission to view billing details. Contact an Admin or Owner for access.',
    ),
    unavailable: translate('Billing details are not available for this card.'),
    error: translate("We couldn't load billing details. Try again later."),
  }
  const companyLineCopy: GroupCopy = {
    loading: translate('Loading company line'),
    unavailableRole: translate(
      'You do not have permission to view the company line. Contact an Admin or Owner for access.',
    ),
    unavailable: translate('The company line is not available for this card.'),
    error: translate("We couldn't load the company line. Try again later."),
  }

  // Address line: `line1 · line2`, dropping the separator and second part when
  // `line2` is null. Nullable fields are omitted, never rendered as '' or 'null'.
  const addressLine: ReactNode =
    billingContact !== undefined
      ? billingContact.billingAddress.line2 !== null
        ? `${billingContact.billingAddress.line1} · ${billingContact.billingAddress.line2}`
        : billingContact.billingAddress.line1
      : undefined

  // `''` when city, province and postal are all absent — the line is dropped
  // entirely rather than rendering an empty element.
  const locality: string =
    billingContact !== undefined
      ? localityLine(billingContact.billingAddress)
      : ''

  return (
    <Box className={className} data-testid="cardOverviewCardDetails">
      <h2 css={style.title}>{translate('Card details')}</h2>

      <dl css={style.rows}>
        <DetailRow
          testId="cardOverviewCardDetails-cardNumber"
          label={translate('Card number')}
          value={`•••• •••• •••• ${last4}`}
          mono
        />
        <DetailRow
          testId="cardOverviewCardDetails-expiration"
          label={translate('Expiration')}
          value={expiration}
          mono
        />
        {billingState === 'ready' &&
          billingContact !== undefined &&
          /* Nullable — omit the row entirely rather than render an empty ZIP. */
          billingContact.billingAddress.postal !== null && (
            <DetailRow
              testId="cardOverviewCardDetails-zip"
              label={translate('Zip code')}
              value={billingContact.billingAddress.postal}
              mono
            />
          )}

        {companyLineState === 'ready' && companyLine !== undefined && (
          <DetailRow
            testId="cardOverviewCardDetails-companyLine"
            label={translate('Company line')}
            value={companyLine.name}
          />
        )}
      </dl>

      <GroupState
        state={billingState}
        reasonCode={billingReasonCode}
        copy={billingCopy}
        testId="cardOverviewCardDetails-billing"
      />
      <GroupState
        state={companyLineState}
        reasonCode={companyLineReasonCode}
        copy={companyLineCopy}
        testId="cardOverviewCardDetails-companyLine"
      />

      {billingState === 'ready' && billingContact !== undefined && (
        <div css={style.block} data-testid="cardOverviewCardDetails-cardholder">
          <span css={style.blockKicker}>
            {translate('Cardholder & billing')}
          </span>
          <span css={style.blockName}>{billingContact.nameLine1}</span>
          {/* Nullable — its own line only when present. */}
          {billingContact.nameLine2 !== null && (
            <span css={style.blockSub}>{billingContact.nameLine2}</span>
          )}
          <span css={style.blockSub}>{addressLine}</span>
          {/* City, province and postal on their own line, per the design. */}
          {locality !== '' && (
            <span
              css={style.blockSub}
              data-testid="cardOverviewCardDetails-locality"
            >
              {locality}
            </span>
          )}
        </div>
      )}

      <div css={style.footer}>
        <span css={style.footerText}>
          {translate('Reveal & copy the full number in the card header ↑')}
        </span>
        <StatNavLink href={manageHref} onNavigate={onNavigate}>
          {translate('Manage in Controls & Settings →')}
        </StatNavLink>
      </div>
    </Box>
  )
}
